import { Injectable } from '@angular/core';

import { TotalsPrefix } from '@cds/betting-offer';
import { MessageType } from '@cds/push';
import { EventOption } from '@frontend/sports/betting-offer/feature/model';
import { TeaserBetConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { isGameUpdateEnvelope, isOptionMarketUpdateEnvelope } from '@frontend/sports/content-distribution/feature';
import { convertFromBposTeaserPrice } from '@frontend/sports/odds/feature';
import { BetslipDisplayMode } from '@frontend/sports/types/components/bet-placement';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { round } from 'lodash-es';
import { EventDetailsService } from 'packages/sports/web/app/src/event-details-common/event-details.service';
import { concatMap, filter, from, map, merge, mergeMap, switchMap } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState, IBetslipState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { flattenPicks } from '../../../core/utils';
import { BetslipPlaceBetActions } from '../../betplacement/actions';
import BetslipPickActions from '../../picks/actions';
import { addGroup, replaceGroup } from '../../picks/grouped-picks.actions';
import { ofTypeReplaceExistingPick } from '../../picks/helpers';
import { betslipPicksListSelector } from '../../picks/selectors';
import { BetslipTypesActions } from '../../types/actions';
import { selectTeaserPicksState } from '../../types/selectors';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipTeaserActions } from '../actions';
import { BetslipTeaserPickPointsTrackingData, BetslipTeaserTrackingData, IAddSportPickPayload, TrackingEvent } from '../models';
import { BetslipTeaserBetPlaceBetActions } from '../place-bet.actions';
import { teaserBetStateSelector } from '../selectors';
import { TeaserBetFilterService } from '../services/teaser-bet-filter.service';
import { TeaserBetNewService } from '../services/teaser-bet-new.service';
import { ITeaserBetPickState } from '../state';
import { TeaserBetTrackingActions } from '../teaser-bet-tracking.actions';

type AllowedTeaserPickTypes = BetslipV1Pick | BetslipV2OptionMarketPick;

@Injectable()
export class TeaserBetEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private teaserBetConfig: TeaserBetConfig,
        private teaserFilterService: TeaserBetFilterService,
        private eventDetailsService: EventDetailsService,
        private teaserBetService: TeaserBetNewService,
    ) {}

    //Add ITeaserBetPickState to Teaser picks type state when adding a pick to a group.
    handleAddTypeGroupPickEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(replaceGroup, addGroup),
            concatLatestFrom(() => this.store.select(selectTeaserPicksState)),
            concatMap(([action, state]) => {
                const picks = action.newPick.getLegs();
                const existingPicks = Object.keys(state.picks);

                // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
                return picks
                    .filter((pick) => !existingPicks.includes(pick.id.toString()))
                    .map((pickId, i) =>
                        BetslipTeaserActions.addGroupPickLeg({
                            newPick: pickId,
                            order: existingPicks.length + i,
                        }),
                    );
            }),
        );
    });

    handleAddPickActionEffect$ = createEffect(() => {
        const effect = this;

        const groupPickLeg$ = this.actions$.pipe(ofType(BetslipTeaserActions.addGroupPickLeg));
        const replacePick$ = this.actions$.pipe(ofTypeReplaceExistingPick());

        const allowedPickTypes = (
            action: Action<string> & { newPick: BetslipPick; order?: number },
        ): action is Action<string> & {
            newPick: AllowedTeaserPickTypes;
            order?: number;
        } => this.teaserBetConfig.isEnabled && (BetslipV1Pick.isPick(action.newPick) || BetslipV2OptionMarketPick.isPick(action.newPick));

        return merge(groupPickLeg$, replacePick$).pipe(
            filter(allowedPickTypes),
            map((action) => ({
                pick: action.newPick,
                order: action.order,
            })),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([{ pick }, betslipState]) =>
                BetslipV1Pick.isPick(pick)
                    ? this.teaserFilterService.isValidV1Pick(pick, betslipState)
                    : this.teaserFilterService.isValidV2Pick(pick, betslipState),
            ),
            map(([{ pick, order }, betslipState]) => ({
                pickPayload: this.toSportPickPayload(pick),
                order,
                betslipState,
            })),
            mergeMap(({ pickPayload, order, betslipState }) =>
                from(this.eventDetailsService.getEvent(pickPayload.fixtureId)).pipe(
                    map((event) => ({
                        event,
                        pickPayload,
                        betslipState,
                        order,
                    })),
                ),
            ),
            mergeMap(function* (result) {
                if (!result.event) {
                    yield BetslipTeaserActions.removePick({ pickId: result.pickPayload.pickId });

                    return;
                }

                const eventOption = result.event.optionGroups.flatMap((group) => group.options).find((o) => o.id === result.pickPayload.optionId);
                if (!eventOption) {
                    yield BetslipTeaserActions.removePick({ pickId: result.pickPayload.pickId });

                    return;
                }

                const balancedPickInfo = effect.teaserFilterService.isBalancedOption(result.event, eventOption);
                if (!balancedPickInfo) {
                    yield BetslipTeaserActions.removePick({ pickId: result.pickPayload.pickId });

                    return;
                }

                const pickList = result.betslipState.picks.pickList;

                const pick = flattenPicks(pickList).find((p) => p.id.toString() === result.pickPayload.pickId);

                if (!pick) {
                    return;
                }

                yield BetslipTeaserActions.addUpdatePick({
                    payload: {
                        pick,
                        selectedOption: eventOption,
                        order: result.order,
                    },
                });
            }),
        );
    });

    handleInconsistentTeaserPick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTeaserActions.addUpdatePick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            mergeMap(function* ([action, state]) {
                const pickId = action.payload?.pick?.id?.toString();
                if (!pickId) {
                    return;
                }
                const pickList = state.picks?.pickList ?? [];

                const flattenedList = flattenPicks(pickList);

                const pick = flattenedList.find((p) => p.id.toString() === pickId);
                if (!pick) {
                    yield BetslipTeaserActions.removePick({ pickId });
                }
            }),
        );
    });

    handleRemovePick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, state]) => this.isTeaserActive(state)),
            mergeMap(function* ([action]) {
                const pickId = action.pickId.toString();
                yield BetslipTeaserActions.removePick({ pickId });
                const trackingData: BetslipTeaserTrackingData = {
                    action: TrackingEvent.Load,
                    positionEvent: 'remove bet',
                    eventDetails: action.eventName,
                };
                yield TeaserBetTrackingActions.trackTeaserPickRemove({ payload: trackingData });
            }),
        );
    });

    handleTogglePick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.togglePick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, state]) => this.isTeaserActive(state)),
            mergeMap(function* ([action, betslipState]) {
                const pickId = action.pickId.toString();
                const pickInfo = betslipState.picks.pickList.find((betslipStatePick) => betslipStatePick.id.toString() === pickId);
                const pick = betslipState.types.teaserBet.picks[pickId];
                const trackingData: BetslipTeaserTrackingData = {
                    action: pick.isSelected ? TrackingEvent.Checked : TrackingEvent.UnChecked,
                    eventDetails: pickInfo?.eventName.name,
                };
                yield TeaserBetTrackingActions.trackTeaserPickToggle({ payload: trackingData });
            }),
        );
    });

    handleTeaserEventUpdatedActionEffect$ = createEffect(
        () => {
            const effect = this;

            return this.actions$.pipe(
                ofType(BetslipActions.eventUpdate, BetslipActions.fixtureUpdate),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                filter(([_, state]) => state.types.base.currentSelectedType !== BetslipType.EditBet && effect.teaserBetConfig.isEnabled),
                // eslint-disable-next-line require-yield
                mergeMap(function* ([action, betslipState]) {
                    const message = action.message;
                    const command = message.payload;
                    let teaserPicks: ITeaserBetPickState[] = [];
                    if (message?.messageType === MessageType.GameUpdate && isGameUpdateEnvelope(message)) {
                        const picks = betslipState.picks.pickList;
                        const eventPicks = picks.filter(BetslipV1Pick.isPick).filter((p) => command.fixtureId === p.id.eventId);
                        const eventPick = eventPicks.find(
                            (pick) =>
                                message.payload.game.id === pick.market.id &&
                                message.payload.game.results.some((result) => result.id === pick.option.id) &&
                                !!betslipState.types.teaserBet.picks[pick.id.toString()],
                        );

                        if (eventPick) {
                            teaserPicks = Object.values(betslipState.types.teaserBet.picks)?.filter((pick) => pick.eventId === eventPick.event.id);
                        }
                    } else if (message?.messageType === MessageType.OptionMarketUpdate && isOptionMarketUpdateEnvelope(message)) {
                        const picks = betslipState.picks.pickList;
                        const eventPicks = picks.filter(BetslipV2OptionMarketPick.isPick).filter((p) => command.fixtureId === p.id.fixtureId);
                        const eventPick = eventPicks.find(
                            (pick) =>
                                message.payload.optionMarket.id === pick.market.id &&
                                message.payload.optionMarket.options.some((option) => option.id === pick.option.id) &&
                                !!betslipState.types.teaserBet.picks[pick.id.toString()],
                        );

                        if (eventPick) {
                            teaserPicks = Object.values(betslipState.types.teaserBet.picks)?.filter(
                                (pick) => pick.eventId === eventPick.fixture.fixtureId,
                            );
                        }
                    }
                    if (teaserPicks.length) {
                        effect.teaserFilterService.updateEvent(command.fixtureId, command, teaserPicks);
                    }
                }),
            );
        },
        {
            dispatch: false,
        },
    );

    handleTeaserFixtureUpdatedActionEffect$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(BetslipActions.fixtureUpdate),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, state]) => state.types.base.currentSelectedType !== BetslipType.EditBet && effect.teaserBetConfig.isEnabled),
            mergeMap(function* ([action, betslipState]) {
                const message = action.message;
                const command = message.payload;
                if (message?.messageType == MessageType.FixtureUpdate) {
                    const picks = betslipState.picks.pickList;
                    const eventPicks = picks.filter(BetslipV2OptionMarketPick.isPick).filter((p) => command.fixtureId === p.id.fixtureId);
                    if (eventPicks.some((p) => p.isLive)) {
                        const pickIds = eventPicks.map((pick) => pick.id.toString());
                        yield BetslipTeaserActions.removePicks({ pickIds });
                    }
                }
            }),
        );
    });

    handleRemoveMultiplePicks$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removeMultiplePicks),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            mergeMap(function* ([action]) {
                const pickIds = action.pickIds.map((id) => id.toString());
                yield BetslipTeaserActions.removePicks({ pickIds });
            }),
        );
    });

    handleRemovePicksFinished$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTeaserActions.removePicksFinished),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, state]) => this.isTeaserActive(state)),
            map(([action, _]) => action),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            filter(([_, betslip]) => betslip.length > 0),
            concatMap(function* ([_, betslipState]) {
                const betslipType = betslipState.length === 1 ? BetslipType.Single : BetslipType.Combo;
                yield BetslipTypesActions.setSelectedType({ betslipType, selectedByUser: false, systemInfo: null });
            }),
        );
    });

    handleClearTeaserResultsEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                BetslipTeaserActions.addUpdatePick,
                BetslipTeaserActions.removePick,
                BetslipTeaserActions.removePicks,
                BetslipPickActions.togglePick,
                BetslipTypesActions.setSelectedType,
                BetslipPickActions.toggleAllPicks,
            ),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, betslipState]) => betslipState.base.state === BetslipState.Edit && this.isTeaserActive(betslipState)),
            concatMap(function* () {
                yield BetslipTeaserActions.removeLockedOrClosedPicks();
                yield BetslipTeaserBetPlaceBetActions.clearResults();
            }),
        );
    });

    handleRemoveLockedOrClosedPicks$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTeaserActions.removeLockedOrClosedPicks),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, state]) => state.types.teaserBet !== undefined),
            switchMap(function* ([_, state]) {
                const teasedPicksToRemove: ITeaserBetPickState[] = [];
                const teaser = state.types.teaserBet;

                const basePicks = Object.keys(teaser.picks);
                for (const pickId of basePicks) {
                    const teasedOption = teaser.picks[pickId].selectedOption;
                    if (!teasedOption) {
                        continue;
                    }
                }

                if (teasedPicksToRemove.length === 0) {
                    return;
                }

                yield BetslipTeaserActions.removePicks({ pickIds: teasedPicksToRemove.map((pick) => pick.id) });
            }),
        );
    });

    validateBetslip$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                BetslipTeaserActions.updateAlternatePick,
                BetslipTeaserActions.addUpdatePick,
                BetslipPickActions.togglePick,
                BetslipPickActions.toggleAllPicks,
                BetslipTeaserActions.setTeaserBetStake,
            ),
            filter((action: Action & { silent?: boolean }) => !action.silent),
            map(() => BetslipErrorsActions.validateBetslip()),
        );
    });

    updateTeaserPrice = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(
                BetslipTeaserActions.addUpdatePick,
                BetslipTeaserActions.setTeasePicks,
                BetslipTeaserActions.removePick,
                BetslipTeaserActions.removePicks,
                BetslipPickActions.togglePick,
                BetslipPickActions.toggleAllPicks,
                BetslipTeaserActions.togglePick,
            ),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, state]) => state.types.teaserBet !== undefined && this.isTeaserActive(state)),
            mergeMap(([_, betslipState]) =>
                this.teaserBetService.getLatestTeaserPrices().pipe(map((teaserPrices) => ({ teaserPrices, betslipState }))),
            ),
            mergeMap(function* (data) {
                const teaserBetState = data.betslipState.types.teaserBet;
                const picks: { [pickId: string]: { oldPoints: number; newPoints: number } } = {};
                const picksList = flattenPicks(data.betslipState.picks.pickList);
                const sportWisePicks: { sportId: SportConstant; count: number }[] = [];
                const pickTrackDetails: BetslipTeaserPickPointsTrackingData[] = [];

                picksList
                    .filter((p) => !!teaserBetState.picks[p.id.toString()]?.isSelected)
                    .forEach((pick, index) => {
                        const pickState = teaserBetState.picks[pick.id.toString()]!;
                        effect.getSportPicks(sportWisePicks, pick.sportId);
                        const pickPoints = effect.getTeaserPoints(
                            teaserBetState.picks[pick.id.toString()].selectedOption,
                            teaserBetState.selectedPoints,
                            pick.totalsPrefix,
                        );
                        picks[pick.id.toString()] = pickPoints;

                        if (effect.teaserBetConfig.isTeaserPickPriceTrackingEnabled) {
                            const eventDetails = `teaser - ${pickState.selectedOption?.id};${teaserBetState.selectedPoints};${pickPoints.oldPoints};${pickPoints.newPoints};${pick?.totalsPrefix}`;
                            pickTrackDetails.push({ isSelected: pickState.isSelected, eventDetails, index: index + 1 });
                        }
                    });

                const teaserPriceKey = effect.getTeaserPriceKey(sportWisePicks, teaserBetState.selectedPoints);

                if (effect.teaserBetConfig.isTeaserPickPriceTrackingEnabled) {
                    yield TeaserBetTrackingActions.trackTeaserPickPoints({
                        payload: pickTrackDetails,
                    });
                }

                yield BetslipTeaserActions.updateTeaserPrices({
                    odds: convertFromBposTeaserPrice(data.teaserPrices.pointsPrices[teaserPriceKey]),
                    priceVersionId: data.teaserPrices.teaserPriceVersionId,
                    selectedPoints: teaserBetState.selectedPoints,
                    picks,
                });
            }),
        );
    });

    updateTeaserSelectedPrice_TeaserPicksEmpty = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                BetslipPickActions.removePick,
                BetslipPickActions.removeMultiplePicks,
                BetslipTeaserActions.removePick,
                BetslipTeaserActions.removePicks,
            ),
            concatLatestFrom(() => this.store.select(teaserBetStateSelector)),
            filter(([_, state]) => Object.entries(state.picks)?.length === 0),

            concatMap(function* (): Iterable<Action> {
                yield BetslipTeaserActions.setTeasePicks({ keypoint: 0 });
            }),
        );
    });

    private isTeaserActive(state: IBetslipState): boolean {
        return (
            state.types.base.currentSelectedType === BetslipType.Teaser ||
            (state.base.displayMode === BetslipDisplayMode.Linear && state.types.base.linearTypes[BetslipType.Teaser])
        );
    }

    private getSportPicks(sportWisePicks: { sportId: SportConstant; count: number }[], sportId: SportConstant) {
        const sportPick = sportWisePicks.find((x) => x.sportId === sportId);
        if (sportPick) {
            sportPick.count++;
        } else {
            sportWisePicks.push({ sportId, count: 1 });
        }
    }

    private getTeaserPriceKey(sportWisePicks: { sportId: SportConstant; count: number }[], selectedPoints: number) {
        let teaserPriceKey = '';
        sportWisePicks
            .sort((a, b) => a.sportId - b.sportId)
            .forEach(function (pick) {
                teaserPriceKey += pick.sportId + '_' + pick.count + ';';
            });

        teaserPriceKey += selectedPoints;

        return teaserPriceKey;
    }

    private toNumber(value: string | undefined): number | null {
        const num = Number(value?.replace(',', '.').replace('+', ''));
        return isNaN(num) ? null : num;
    }

    private getTeaserPoints(
        option: EventOption | undefined,
        teasedPoints: number,
        totalsPrefix: TotalsPrefix | undefined,
    ): { oldPoints: number; newPoints: number } {
        if (!option) {
            return { oldPoints: 0, newPoints: 0 };
        }

        const oldPoints = this.toNumber(option.marketAttribute) ?? this.toNumber(option.optionAttribute) ?? NaN;

        return { oldPoints, newPoints: this.getNewPointsAfterTeasing(totalsPrefix, teasedPoints, oldPoints) };
    }

    placeTeaserEmptyResult$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.startedPlaceBet),
            map(() => BetslipTeaserBetPlaceBetActions.clearResults()),
        );
    });

    private getNewPointsAfterTeasing(totalsPrefix: TotalsPrefix | undefined, teasedPoints: number, oldPoints: number): number {
        const newTeasedPoints = totalsPrefix === TotalsPrefix.Over ? oldPoints - teasedPoints : teasedPoints + oldPoints;

        return round(newTeasedPoints, 1);
    }

    private toSportPickPayload(pick: BetslipV1Pick | BetslipV2OptionMarketPick): IAddSportPickPayload {
        if (BetslipV1Pick.isPick(pick)) {
            return {
                pickId: pick.id.toString(),
                fixtureId: pick.event.id,
                optionId: pick.option.id,
            };
        }

        return {
            pickId: pick.id.toString(),
            fixtureId: pick.fixture.fixtureId,
            optionId: pick.option.id,
        };
    }
}
